import type { CSSProperties } from "react";
import { useState } from "react";
import type { Scenario } from "../lib/scenarios";

function formatUsd(n: number): string {
  if (!isFinite(n)) return "—";
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(n) < 10 ? 2 : 0,
  });
  return neg ? `−${abs}` : `+${abs}`;
}

function formatPrice(n: number): string {
  if (!isFinite(n)) return "—";
  if (n >= 10_000) return `$${(n / 1000).toFixed(2)}K`;
  if (n >= 1000) return `$${n.toFixed(0)}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toPrecision(3)}`;
}

const th: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 8,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--fg-dim)",
  padding: "6px 8px",
  textAlign: "right",
  borderBottom: "1px solid var(--line-2)",
  position: "sticky",
  top: 0,
  background: "var(--bg-1)",
  fontWeight: 500,
};

const td: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 11,
  padding: "6px 8px",
  height: 28,
  textAlign: "right",
  color: "var(--fg-hi)",
  borderBottom: "1px solid var(--line-1)",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

const toneColor: Record<NonNullable<Scenario["tone"]>, string> = {
  mark: "rgba(194,214,255,0.8)",
  threshold: "#60a5fa",
  liq: "var(--sev-crit)",
  gain: "var(--fg-meta)",
  loss: "var(--fg-meta)",
  neutral: "var(--fg-meta)",
};

export function ScenariosTable({
  rows,
  activePrice,
  onHover,
  onSelect,
}: {
  rows: ReadonlyArray<Scenario>;
  activePrice?: number | null;
  onHover?: (s: Scenario | null) => void;
  onSelect?: (s: Scenario) => void;
}) {
  const [rowHover, setRowHover] = useState<number | null>(null);

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        position: "relative",
      }}
      onMouseLeave={() => {
        setRowHover(null);
        onHover?.(null);
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left" }}>SCENARIO</th>
            <th style={th}>PRICE</th>
            <th style={th}>PERP</th>
            <th style={th}>PRED</th>
            <th style={{ ...th, paddingRight: 14 }}>TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const totalColor =
              r.totalPnl > 0
                ? "var(--up)"
                : r.totalPnl < 0
                  ? "var(--dn)"
                  : "var(--fg-hi)";
            const labelColor = r.tone ? toneColor[r.tone] : "var(--fg-hi)";
            const isHover = rowHover === r.price;
            const isActive =
              activePrice !== null &&
              activePrice !== undefined &&
              Math.abs(activePrice - r.price) < 1e-6;
            const rowBg = isActive
              ? "rgba(158, 203, 255, 0.12)"
              : isHover
                ? "var(--bg-2)"
                : "transparent";
            const rowShadow = isActive
              ? "inset 2px 0 0 var(--brand)"
              : undefined;
            return (
              <tr
                key={`${r.label}-${r.price}`}
                onMouseEnter={() => {
                  setRowHover(r.price);
                  onHover?.(r);
                }}
                onClick={() => onSelect?.(r)}
                style={{
                  cursor: onSelect ? "pointer" : "default",
                  background: rowBg,
                  boxShadow: rowShadow,
                  transition: "background 120ms ease",
                }}
              >
                <td
                  style={{
                    ...td,
                    textAlign: "left",
                    color: labelColor,
                    fontWeight:
                      r.tone === "mark" ||
                      r.tone === "threshold" ||
                      r.tone === "liq"
                        ? 500
                        : 400,
                    letterSpacing: "0.08em",
                  }}
                >
                  {r.label}
                </td>
                <td style={td}>{formatPrice(r.price)}</td>
                <td
                  style={{
                    ...td,
                    color: r.perpPnl >= 0 ? "var(--up)" : "var(--dn)",
                  }}
                >
                  {formatUsd(r.perpPnl)}
                </td>
                <td
                  style={{
                    ...td,
                    color: r.predPnl >= 0 ? "var(--up)" : "var(--dn)",
                  }}
                >
                  {formatUsd(r.predPnl)}
                </td>
                <td
                  style={{
                    ...td,
                    color: totalColor,
                    fontWeight: 500,
                    paddingRight: 14,
                  }}
                >
                  {formatUsd(r.totalPnl)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
